import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Shield, Zap, FileText, BarChart2, Mail, BookOpen, TrendingUp, Presentation, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import HeroVideo from '../../components/HeroVideo';
import InlineVideo from '../../components/InlineVideo';


const BG = '#05060f';
const CARD_BG = '#0a0d1e';
const BORDER = '#1a2040';
const WHITE = '#FFFFFF';
const TEAL = '#2DC896';
const AMBER = '#EF9F27';
const MUTED = '#8a9ab5';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const LandingPageAIExecution = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e?.preventDefault();
    if (!waitlistEmail?.trim()) return;
    setWaitlistSubmitting(true);
    setWaitlistError('');
    try {
      const { error } = await supabase?.from('waitlist')?.insert({
        email: waitlistEmail?.trim(),
        created_at: new Date()?.toISOString(),
      });
      if (error) {
        if (error?.code === '23505') {
          setWaitlistError('This email is already on the list.');
        } else {
          setWaitlistError('Something went wrong. Try again.');
        }
      } else {
        setWaitlistSubmitted(true);
      }
    } catch (err) {
      setWaitlistError('Something went wrong. Try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // ── SECTION DATA ──────────────────────────────────────────────────────────

  // WHAT GETS EXECUTED — rewritten for commercial clarity
  const deliverables = [
    {
      icon: BarChart2,
      title: 'Market Intelligence Reports',
      desc: 'Competitor analysis, opportunity gaps, and positioning insights structured for immediate use.'
    },
    {
      icon: Zap,
      title: 'AI Copy Systems',
      desc: 'Ads, landing pages, and conversion assets built around your offer and audience.'
    },
    {
      icon: Mail,
      title: 'Sales Asset Bundles',
      desc: 'Cold outreach sequences, follow-up scripts, and objection-handling flows ready to deploy.'
    },
    {
      icon: BookOpen,
      title: 'Content Systems',
      desc: '30-day content plans with pillars, hooks, post ideas, and a weekly publishing schedule.'
    },
    {
      icon: TrendingUp,
      title: 'Offer + Hiring Strategy',
      desc: 'Packaging, pricing, and team structure designed to support growth and strong margins.'
    },
    {
      icon: Presentation,
      title: 'Strategy + Founder Tools',
      desc: 'Business plans, investor narratives, and decision frameworks built for clarity and action.'
    }
  ];

  // BUILT FOR FOUNDERS, AGENCIES, AND OPERATORS — rewritten
  const operators = [
    {
      label: 'Solo Founders',
      desc: 'Run at agency output without the agency overhead. Execute faster across every function.'
    },
    {
      label: 'Agencies',
      desc: 'Deliver more client work in less time. Scale throughput without scaling headcount.'
    },
    {
      label: 'Creators',
      desc: 'Turn expertise into systems. Build content, offers, and audiences without the grind.'
    },
    {
      label: 'Operators',
      desc: 'Replace hours of manual work with structured execution on demand.'
    }
  ];

  // BUILT ON REAL INFRASTRUCTURE — sharpened
  const infrastructure = [
    {
      icon: Shield,
      title: 'Secured and private',
      desc: 'Your data and mission context stay yours. Encrypted and never shared beyond your workspace.'
    },
    {
      icon: Zap,
      title: 'Live execution architecture',
      desc: 'Every mission runs in real time against current data — not cached, not templated.'
    },
    {
      icon: FileText,
      title: 'Downloadable deliverables',
      desc: 'Outputs are structured files you can use immediately — not chat logs, not rough notes.'
    }
  ];

  // RUN MISSIONS LIKE THESE — new section
  const exampleMissions = [
    {
      mission: 'Write a 5-email cold outreach system for my service business',
      returns: 'Email sequence · follow-up cadence · objection handling · CTA structure'
    },
    {
      mission: 'Audit my funnel and identify what\'s blocking conversions',
      returns: 'Issue diagnosis · fix priorities · messaging gaps · conversion recommendations'
    },
    {
      mission: 'Turn my rough idea into a story-driven content campaign for my brand',
      returns: 'Campaign angle · messaging direction · content themes · hooks · captions · rollout ideas'
    },
    {
      mission: 'Create a 30-day content engine for my offer',
      returns: 'Content pillars · hooks · post ideas · captions · weekly schedule'
    }
  ];

  const navLinks = [
    { label: 'HOME', path: '/' },
    { label: 'COMMAND', path: '/command-center-ai-execution-interface' },
    { label: 'LEADS', path: '/lead-command-center' },
    { label: 'OPERATIONS', path: '/operations' },
    { label: 'PRICING', path: '/pricing' }
  ];

  const footerLinks = ['PRIVACY', 'TERMS', 'CONTACT', 'PRICING', 'AFFILIATE', 'SIGN UP', 'LOGIN'];
  const footerPaths = {
    PRIVACY: '/privacy',
    TERMS: '/terms',
    CONTACT: '/contact',
    AFFILIATE: '/affiliate-program',
    PRICING: '/pricing',
    'SIGN UP': '/user-registration',
    LOGIN: '/user-login'
  };

  return (
    <div className="min-h-screen" style={{ background: BG, color: WHITE, fontFamily: MONO }}>
      <style>{`
        @keyframes amberPulse {
          0%, 89%, 100% { box-shadow: 0 0 10px rgba(239,159,39,0.45), 0 0 18px rgba(239,159,39,0.3); }
          90%, 95% { box-shadow: 0 0 18px rgba(239,159,39,0.75), 0 0 32px rgba(239,159,39,0.5); }
        }
      `}</style>
      <Helmet>
        <title>RecallCore — Hours of business work. Finished in minutes.</title>
        <meta name="description" content="82+ AI specialists. Ready to deploy. State your mission. RecallCore deploys the right specialists and returns finished work ready to use." />
        <meta property="og:title" content="RecallCore — Hours of business work. Finished in minutes." />
        <meta property="og:description" content="82+ AI specialists. Ready to deploy. State your mission. RecallCore deploys the right specialists and returns finished work ready to use." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://recall6043.builtwithrocket.new" />
        <meta property="og:image" content="https://recall6043.builtwithrocket.new/assets/images/RecallCore_Logo-1772366339623.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="RecallCore — Hours of business work. Finished in minutes." />
        <meta name="twitter:description" content="82+ AI specialists. Ready to deploy. State your mission. RecallCore deploys the right specialists and returns finished work ready to use." />
        <meta name="twitter:image" content="https://recall6043.builtwithrocket.new/assets/images/RecallCore_Logo-1772366339623.png" />
        <link rel="canonical" href="https://recall6043.builtwithrocket.new" />
      </Helmet>

      {/* ── NAVIGATION ──────────────────────────────────────────────────────── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(5,6,15,0.97)' : 'rgba(5,6,15,0.85)',
          backdropFilter: 'blur(14px)',
          borderBottom: `1px solid ${BORDER}`
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/assets/images/RecallCore_Logo-1772366339623.png"
              alt="RecallCore logo — golden neural circuit wheel"
              className="w-8 h-8 object-cover"
              style={{ borderRadius: '2px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1.1rem', color: WHITE }}>
              RecallCore<sup style={{ fontSize: '0.6rem', color: TEAL }}>™</sup>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {navLinks?.map((item) => (
              <button
                key={item?.path}
                onClick={() => navigate(item?.path)}
                style={{ color: MUTED, fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.color = WHITE}
                onMouseLeave={(e) => e.currentTarget.style.color = MUTED}
              >
                {item?.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/command-center-ai-execution-interface')}
            style={{
              background: AMBER,
              color: BG,
              padding: '8px 18px',
              fontFamily: MONO,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              animation: 'amberPulse 6s infinite'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5a832'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = AMBER; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            LAUNCH MISSION
          </button>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="pt-40 pb-36 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p style={{ color: MUTED, fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '2rem' }}>
            5 MINUTES IN RECALLCORE VS 5+ HOURS OF SCATTERED MANUAL WORK
            </p>
            {/* ✦ NEW HEADLINE */}
            <h1 style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: 'clamp(2.6rem, 7.5vw, 5rem)', lineHeight: 1.08, marginBottom: '1.5rem', color: WHITE, letterSpacing: '-0.01em' }}>
              TURN INTENT INTO <br /> FINISHED BUSINESS EXECUTION.
            </h1>
            {/* ✦ NEW SUBHEADLINE */}
            <p style={{ color: MUTED, fontFamily: MONO, fontSize: '0.92rem', lineHeight: 1.8, maxWidth: '42rem', margin: '0 auto 1.4rem' }}>
              Research. Strategy. Plan. System. Execution.<br />Delivered.
            </p>
            {/* ✦ NEW PROOF LINE */}
            <p style={{ color: TEAL, fontFamily: MONO, fontSize: '0.82rem', letterSpacing: '0.08em', marginBottom: '3rem', fontWeight: 600 }}>
              82+ AI specialists. Ready to get real work done.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/user-login')}
                style={{ background: AMBER, color: BG, padding: '15px 34px', fontFamily: MONO, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Launch Your First Mission →
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/pricing')}
                style={{ background: 'transparent', color: TEAL, padding: '15px 34px', fontFamily: MONO, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: `1px solid ${TEAL}`, borderRadius: '6px', cursor: 'pointer' }}
              >
                VIEW PRICING
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PROOF BAR ───────────────────────────────────────────────────────── */}
      <div style={{ background: CARD_BG, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: '18px 24px', textAlign: 'center' }}>
        <p style={{ color: MUTED, fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
         Command Center for founders who need work finished.
        </p>
      </div>

      {/* ── SECTION 2 — FROM MISSION TO OUTPUT ──────────────────────────────── */}
      <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-16">
            {/* ✦ RENAMED */}
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: WHITE, marginBottom: '1rem' }}>FROM MISSION TO RESULTS</h2>
            {/* ✦ UPDATED SUPPORT LINE */}
            <p style={{ color: TEAL, fontFamily: MONO, fontSize: '0.9rem', letterSpacing: '0.06em' }}>Run the Mission. Get the Assets. Move the Business.</p>
          </motion.div>
          {/* ✦ UPDATED 4 BULLETS */}
          <div className="flex flex-col gap-4">
            {[
              'Give RecallCore one mission — no prompt engineering required.',
              'RecallCore deploys the right AI specialists for the job.',
              'Outputs come back execution-ready — structured, complete, and clear.',
              'The work is usable immediately.'
            ]?.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="flex items-center gap-5 px-7 py-5"
                style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
              >
                <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
                <span style={{ fontFamily: MONO, color: WHITE, fontSize: '0.88rem', fontWeight: 600 }}>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3 — HOW IT WORKS ────────────────────────────────────────── */}
      <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: WHITE }}>HOW IT WORKS</h2>
          </motion.div>
          <InlineVideo src="/videos/hero-1.mp4" />
          {/* ✦ REWRITTEN 3 STEPS */}
          <div className="flex flex-col gap-5">
            {[
              {
                step: '01',
                title: 'STATE THE MISSION',
                desc: 'Type what you need done in one sentence. '
              },
              {
                step: '02',
                title: 'RECALLCORE EXECUTES',
                desc: 'The right AI specialist steps in, takes the task, and builds the work forward.'
              },
              {
                step: '03',
                title: 'GET USABLE RESULTS',
                desc: 'Receive structured outputs as clear deliverables — ready to review, refine, and use right away.'
              }
            ]?.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex gap-7 items-start p-8"
                style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
              >
                <span style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: '2.8rem', color: WHITE, flexShrink: 0, lineHeight: 1 }}>{item?.step}</span>
                <div>
                  <h3 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1rem', color: WHITE, marginBottom: '0.6rem', letterSpacing: '0.08em' }}>{item?.title}</h3>
                  <p style={{ color: MUTED, fontSize: '0.85rem', fontFamily: MONO, lineHeight: 1.7 }}>{item?.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 4 — WHAT GETS EXECUTED ─────────────────────────────────── */}
      <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: WHITE, marginBottom: '1rem' }}>WHAT GETS EXECUTED</h2>
            <p style={{ color: TEAL, fontFamily: MONO, fontSize: '0.9rem', letterSpacing: '0.06em' }}>Every mission produces real, usable deliverables.</p>
          </motion.div>
          {/* ✦ REWRITTEN CARDS */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {deliverables?.map((item, i) => {
              const Icon = item?.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  viewport={{ once: true }}
                  className="p-7"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <Icon className="w-5 h-5 mb-5" style={{ color: TEAL }} />
                  <h3 style={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.88rem', color: WHITE, marginBottom: '0.6rem' }}>{item?.title}</h3>
                  <p style={{ color: MUTED, fontSize: '0.78rem', lineHeight: 1.7, fontFamily: MONO }}>{item?.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
            {/* ── SECTION 4B — RUN MISSIONS LIKE THESE (NEW) ──────────────────────── */}
      <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-16">
            {/* ✦ NEW SECTION TITLE */}
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: WHITE, marginBottom: '1rem' }}>RUN MISSIONS LIKE THESE</h2>
            <p style={{ color: TEAL, fontFamily: MONO, fontSize: '0.9rem', letterSpacing: '0.06em' }}>See the kind of work RecallCore can return in minutes.</p>
          </motion.div>
          {/* ✦ 4 EXAMPLE MISSION CARDS */}
          <div className="grid sm:grid-cols-2 gap-5">
            {exampleMissions?.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="p-7"
                style={{ background: CARD_BG, border: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: '1rem' }}
              >
                {/* Mission label */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <span style={{ color: AMBER, fontFamily: MONO, fontWeight: 900, fontSize: '0.8rem', flexShrink: 0, marginTop: '1px' }}>
                    MISSION {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                {/* Mission text */}
                <p style={{ fontFamily: MONO, fontWeight: 600, fontSize: '0.88rem', color: WHITE, lineHeight: 1.6, margin: 0 }}>
                  {item?.mission}
                </p>
                {/* Returns line */}
                <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: '0.9rem' }}>
                  <p style={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: TEAL, marginBottom: '0.35rem', fontWeight: 700 }}>
                    RETURNS:
                  </p>
                  <p style={{ fontFamily: MONO, fontSize: '0.77rem', color: MUTED, lineHeight: 1.7, margin: 0 }}>
                    {item?.returns}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <HeroVideo src="/videos/hero-2.mp4" sectionTitle="RESULTS" sectionSubtitle="Real outputs. Real deliverables. Ready to deploy." />

      {/* ── SECTION 5 — BUILT FOR FOUNDERS, AGENCIES, AND CREATORS ─────────── */}
      <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-16">
            {/* ✦ RENAMED */}
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)', color: WHITE }}>BUILT FOR FOUNDERS, AGENCIES, AND CREATORS</h2>
          </motion.div>
          {/* ✦ REWRITTEN BOXES */}
          <div className="grid sm:grid-cols-2 gap-5">
            {operators?.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="p-7"
                style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
                  <span style={{ color: AMBER, fontFamily: MONO, fontWeight: 900, fontSize: '1.1rem', flexShrink: 0, lineHeight: 1 }}>→</span>
                  <span style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '0.82rem', color: WHITE, letterSpacing: '0.06em' }}>{item?.label}</span>
                </div>
                <p style={{ fontFamily: MONO, fontSize: '0.82rem', color: MUTED, lineHeight: 1.7, margin: 0 }}>{item?.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 6 — BUILT ON REAL INFRASTRUCTURE ────────────────────────── */}
      <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: WHITE }}>BUILT ON REAL INFRASTRUCTURE</h2>
          </motion.div>
          {/* ✦ SHARPENED CARDS */}
          <div className="grid sm:grid-cols-3 gap-5">
            {infrastructure?.map((item, i) => {
              const Icon = item?.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-7"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <Icon className="w-5 h-5 mb-5" style={{ color: TEAL }} />
                  <h3 style={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.9rem', color: WHITE, marginBottom: '0.6rem' }}>{item?.title}</h3>
                  <p style={{ color: MUTED, fontSize: '0.8rem', lineHeight: 1.7, fontFamily: MONO }}>{item?.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

         {/* ── SECTION 7 — FINAL CTA ───────────────────────────────────────────── */}
         <section className="py-36 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}>
            {/* ✦ NEW CTA HEADLINE */}
            <h2 style={{ fontFamily: ORBITRON, fontWeight: 900, fontSize: 'clamp(2rem, 5.5vw, 3.8rem)', color: WHITE, marginBottom: '1.2rem', lineHeight: 1.1 }}>
              READY TO EXECUTE<br />YOUR NEXT MOVE?
            </h2>
            {/* ✦ NEW SUPPORTING LINE */}
            <p style={{ color: TEAL, fontFamily: MONO, fontSize: '0.9rem', letterSpacing: '0.06em', marginBottom: '2.5rem', fontWeight: 600 }}>
              Your first mission takes minutes. The output can save hours.
            </p>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/user-login')}
              style={{ background: AMBER, color: BG, padding: '15px 34px', fontFamily: MONO, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'inline-block', marginBottom: '1.5rem' }}
            >
              Launch Your First Mission →
            </motion.button>
            <p style={{ color: MUTED, fontSize: '0.72rem', fontFamily: MONO }}>Join founders and operators running their workflow on RecallCore.</p>
          </motion.div>
        </div>
      </section>

      {/* ── WAITLIST SECTION ────────────────────────────────────────────────── */}
      {/* ✦ REPOSITIONED: optional updates, not primary action */}
      <section style={{ padding: '60px 24px 80px', borderTop: `1px solid ${BORDER}`, position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            style={{
              background: '#0a0f0d',
              border: '1px solid rgba(0, 255, 180, 0.25)',
              borderRadius: 12,
              padding: '40px',
              boxShadow: '0 0 20px rgba(0, 255, 180, 0.08)',
            }}
          >
            <p style={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: '0.2em',
              color: '#00ffb4',
              opacity: 0.7,
              textTransform: 'uppercase',
              marginBottom: 12,
              marginTop: 0,
            }}>
              STAY ON THE LINE
            </p>
            {/* ✦ SOFTENED — optional updates, not a gate */}
            <h2 style={{
              fontFamily: ORBITRON,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '0.05em',
              color: WHITE,
              textTransform: 'uppercase',
              marginBottom: 12,
              marginTop: 0,
            }}>
              Get platform updates
            </h2>
            <p style={{
              fontFamily: MONO,
              fontSize: 13,
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.8,
              marginBottom: 28,
              marginTop: 0,
            }}>
              New capabilities, mission flows, and major upgrades — delivered to your inbox when they go live.
            </p>
            {waitlistSubmitted ? (
              <motion.p
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                style={{
                  fontFamily: MONO,
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#00ffb4',
                  margin: 0,
                  textShadow: '0 0 20px rgba(0, 255, 180, 0.4)',
                }}
              >
                You're in. We'll keep you posted.
              </motion.p>
            ) : (
              <form onSubmit={handleWaitlistSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e?.target?.value)}
                  required
                  style={{
                    fontFamily: MONO,
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(0, 255, 180, 0.2)',
                    borderRadius: 6,
                    padding: '12px 16px',
                    color: WHITE,
                    fontSize: 14,
                    outline: 'none',
                  }}
                />
                <motion.button
                  type="submit"
                  disabled={waitlistSubmitting}
                  whileHover={{ scale: waitlistSubmitting ? 1 : 1.02, boxShadow: '0 0 24px rgba(0, 255, 180, 0.25)' }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    fontFamily: MONO,
                    background: '#00ffb4',
                    border: 'none',
                    borderRadius: 6,
                    padding: '12px 24px',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#000000',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: waitlistSubmitting ? 'not-allowed' : 'pointer',
                    opacity: waitlistSubmitting ? 0.7 : 1,
                    width: '100%',
                  }}
                >
                  {waitlistSubmitting ? 'Sending...' : 'Keep me updated'}
                </motion.button>
              </form>
            )}
            {waitlistError && (
              <p style={{
                fontFamily: MONO,
                fontSize: 12,
                color: '#FF6B6B',
                marginTop: 12,
                marginBottom: 0,
              }}>
                {waitlistError}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="py-10 px-6 text-center" style={{ borderTop: `1px solid ${BORDER}`, background: CARD_BG }}>
        <p style={{ color: MUTED, fontSize: '0.7rem', fontFamily: MONO, marginBottom: '1rem' }}>© 2026 RecallCore. All rights reserved.</p>
        <div className="flex flex-wrap justify-center gap-5">
          {footerLinks?.map((link) => (
            <button
              key={link}
              onClick={() => navigate(footerPaths?.[link] || '/')}
              style={{ color: MUTED, fontSize: '0.62rem', fontFamily: MONO, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.12em', transition: 'color 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.color = WHITE}
              onMouseLeave={(e) => e.currentTarget.style.color = MUTED}
            >
              {link}
            </button>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default LandingPageAIExecution;



